import { m } from "~/i18n/messages";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Slider } from "~/components/ui/slider";
import { Skeleton } from "~/components/ui/skeleton";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import {
	useAiPreferencesSuspense,
	useUpdateAiPreferences,
} from "~/lib/client/ai-preferences";
import {
	DEFAULT_AI_PREFERENCES,
	aiPreferencesSchema,
} from "~/lib/server/functions/ai-preference";
import type { AiPreferencesSchema } from "~/lib/server/functions/ai-preference";

function SettingsAiPreferencesSkeleton() {
	return (
		<div className="w-full h-full flex flex-col gap-6">
			<div className="border-b pb-4">
				<Skeleton className="h-6 w-52 mb-2" />
				<Skeleton className="h-4 w-full mb-1" />
				<Skeleton className="h-4 w-3/4" />
			</div>

			{/* Radio groups skeleton */}
			{[1, 2, 3].map((i) => (
				<div key={i} className="space-y-2">
					<Skeleton className="h-5 w-48 mb-3" />
					<div className="space-y-4">
						{[1, 2, 3].map((j) => (
							<div key={j} className="flex items-start gap-3">
								<Skeleton className="h-4 w-4 rounded-full mt-1" />
								<div className="flex-1">
									<Skeleton className="h-5 w-32 mb-1" />
									<Skeleton className="h-4 w-full" />
								</div>
							</div>
						))}
					</div>
				</div>
			))}

			{/* Sliders skeleton */}
			<div className="border-b pb-4 mt-4">
				<Skeleton className="h-6 w-52 mb-2" />
				<Skeleton className="h-4 w-3/4" />
			</div>

			{[1, 2, 3].map((i) => (
				<div key={i} className="space-y-2">
					<div className="flex items-center justify-between w-full">
						<Skeleton className="h-5 w-32" />
						<Skeleton className="h-4 w-10" />
					</div>
					<Skeleton className="h-2 w-full rounded-full my-4" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
				</div>
			))}

			<div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
				<Skeleton className="h-10 w-full sm:w-24" />
				<Skeleton className="h-10 w-full sm:w-40" />
			</div>
		</div>
	);
}

export const Route = createFileRoute("/app/settings/ai-preferences")({
	component: AiPreferencesPage,
});

function AiPreferencesPage() {
	return (
		<SuspenseFallback
			loading={<SettingsAiPreferencesSkeleton />}
			error={
				<div className="p-8 text-red-500">
					{m["settings.ai_preferences.error_loading"]({
						error: "Failed to load AI preferences",
					})}
				</div>
			}
		>
			<AiPreferencesForm />
		</SuspenseFallback>
	);
}

function AiPreferencesForm() {
	const { data: preferences } = useAiPreferencesSuspense();
	const updateMutation = useUpdateAiPreferences();

	const form = useForm({
		defaultValues:
			preferences ?? (DEFAULT_AI_PREFERENCES as AiPreferencesSchema),
		validators: {
			onChange: aiPreferencesSchema,
		},
		onSubmit: ({ value }) =>
			toast.promise(
				updateMutation.mutateAsync({
					top_p: value.top_p,
					temperature: value.temperature,
					repetition_penalty: value.repetition_penalty,
					vocabulary_focus: value.vocabulary_focus,
					challenge_level: value.challenge_level,
				}),
				{
					loading: m["settings.ai_preferences.toast.saving"](),
					success: m["settings.ai_preferences.toast.saved_success"](),
					error: m["settings.ai_preferences.toast.save_failed"](),
				},
			),
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
			className="w-full h-full flex flex-col gap-6"
		>
			<div className="border-b pb-4">
				<h2 className="font-medium">
					{m["settings.ai_preferences.behaviour_settings.title"]()}
				</h2>
				<p className="text-muted-foreground text-sm mt-1">
					{m["settings.ai_preferences.behaviour_settings.description"]()}
				</p>
			</div>
			<form.Field
				name="vocabulary_focus"
				validators={{ onChange: aiPreferencesSchema.shape.vocabulary_focus }}
				children={(field) => (
					<div className="space-y-2 lg:space-y-0">
						<Label className="flex-col items-start">
							{m["settings.ai_preferences.vocabulary_focus.label"]()}
							<RadioGroup
								value={field.state.value}
								onBlur={field.handleBlur}
								onValueChange={(value) =>
									field.handleChange(
										value as AiPreferencesSchema["vocabulary_focus"],
									)
								}
								className="mt-3 space-y-2 lg:space-y-0"
							>
								<div className="flex items-start gap-3">
									<RadioGroupItem
										value="textbook"
										id="textbook"
										className="mt-1"
									/>
									<Label
										htmlFor="textbook"
										className="cursor-pointer flex flex-col lg:flex-row items-start lg:items-center"
									>
										🏫{" "}
										{m[
											"settings.ai_preferences.vocabulary_focus.textbook.label"
										]()}
										<span className="text-sm font-normal text-muted-foreground">
											{m[
												"settings.ai_preferences.vocabulary_focus.textbook.description"
											]()}
										</span>
									</Label>
								</div>
								<div className="flex items-start gap-3">
									<RadioGroupItem value="casual" id="casual" className="mt-1" />
									<Label
										htmlFor="casual"
										className="cursor-pointer flex flex-col lg:flex-row items-start lg:items-center"
									>
										🗣{" "}
										{m[
											"settings.ai_preferences.vocabulary_focus.casual.label"
										]()}
										<span className="text-sm font-normal text-muted-foreground">
											{m[
												"settings.ai_preferences.vocabulary_focus.casual.description"
											]()}
										</span>
									</Label>
								</div>
								<div className="flex items-start gap-3">
									<RadioGroupItem value="keigo" id="keigo" className="mt-1" />
									<Label
										htmlFor="keigo"
										className="cursor-pointer flex flex-col lg:flex-row items-start lg:items-center"
									>
										🏯{" "}
										{m[
											"settings.ai_preferences.vocabulary_focus.keigo.label"
										]()}
										<span className="text-sm font-normal text-muted-foreground">
											{m[
												"settings.ai_preferences.vocabulary_focus.keigo.description"
											]()}
										</span>
									</Label>
								</div>
							</RadioGroup>
						</Label>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
							children={([canSubmit, isSubmitting]) =>
								field.state.meta.errors ? (
									<em className="text-sm text-destructive">
										{field.state.meta.errors.join(", ")}
									</em>
								) : null
							}
						/>
					</div>
				)}
			/>
			<form.Field
				name="challenge_level"
				validators={{ onChange: aiPreferencesSchema.shape.challenge_level }}
				children={(field) => (
					<div className="space-y-2 lg:space-y-0">
						<Label className="flex-col items-start">
							{m["settings.ai_preferences.challenge_level.label"]()}
							<RadioGroup
								value={field.state.value}
								onBlur={field.handleBlur}
								onValueChange={(value) =>
									field.handleChange(
										value as AiPreferencesSchema["challenge_level"],
									)
								}
								className="mt-3 space-y-2 lg:space-y-0"
							>
								<div className="flex items-start gap-3">
									<RadioGroupItem
										value="beginner"
										id="beginner"
										className="mt-1"
									/>
									<Label
										htmlFor="beginner"
										className="cursor-pointer flex flex-col lg:flex-row items-start lg:items-center"
									>
										🌱{" "}
										{m[
											"settings.ai_preferences.challenge_level.beginner.label"
										]()}
										<span className="text-sm font-normal text-muted-foreground">
											{m[
												"settings.ai_preferences.challenge_level.beginner.description"
											]()}
										</span>
									</Label>
								</div>
								<div className="flex items-start gap-3">
									<RadioGroupItem
										value="intermediate"
										id="intermediate"
										className="mt-1"
									/>
									<Label
										htmlFor="intermediate"
										className="cursor-pointer flex flex-col lg:flex-row items-start lg:items-center"
									>
										🌿{" "}
										{m[
											"settings.ai_preferences.challenge_level.intermediate.label"
										]()}
										<span className="text-sm font-normal text-muted-foreground">
											{m[
												"settings.ai_preferences.challenge_level.intermediate.description"
											]()}
										</span>
									</Label>
								</div>
								<div className="flex items-start gap-3">
									<RadioGroupItem
										value="advanced"
										id="advanced"
										className="mt-1"
									/>
									<Label
										htmlFor="advanced"
										className="cursor-pointer flex flex-col lg:flex-row items-start lg:items-center"
									>
										🌳{" "}
										{m[
											"settings.ai_preferences.challenge_level.advanced.label"
										]()}
										<span className="text-sm font-normal text-muted-foreground">
											{m[
												"settings.ai_preferences.challenge_level.advanced.description"
											]()}
										</span>
									</Label>
								</div>
							</RadioGroup>
						</Label>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
							children={([canSubmit, isSubmitting]) =>
								field.state.meta.errors ? (
									<em className="text-sm text-destructive">
										{field.state.meta.errors.join(", ")}
									</em>
								) : null
							}
						/>
					</div>
				)}
			/>
			<div className="border-b pb-4 mt-4">
				<h2 className="font-medium">
					{m["settings.ai_preferences.advanced_settings.title"]()}
				</h2>
				<p className="text-muted-foreground text-sm mt-1">
					{m["settings.ai_preferences.advanced_settings.description"]()}
				</p>
			</div>
			<form.Field
				name="temperature"
				validators={{ onChange: aiPreferencesSchema.shape.temperature }}
				children={(field) => (
					<div className="space-y-2 lg:space-y-0">
						<Label className="flex-col items-start gap-2">
							<div className="flex items-center justify-between w-full">
								<span className="block">
									{m["settings.ai_preferences.creativity.label"]()}
								</span>
							</div>
							<span className="block text-sm tabular-nums">
								{field.state.value.toFixed(2)}
							</span>
							<Slider
								value={[field.state.value]}
								onValueChange={(value) =>
									field.handleChange(
										value[0] as AiPreferencesSchema["temperature"],
									)
								}
								onBlur={field.handleBlur}
								min={0}
								max={2}
								step={0.01}
								className="py-2"
							/>
						</Label>
						<small className="text-sm text-muted-foreground block">
							{m["settings.ai_preferences.creativity.description"]()}
						</small>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
							children={([canSubmit, isSubmitting]) =>
								field.state.meta.errors ? (
									<em className="text-sm text-destructive">
										{field.state.meta.errors.join(", ")}
									</em>
								) : null
							}
						/>
					</div>
				)}
			/>
			<form.Field
				name="top_p"
				validators={{ onChange: aiPreferencesSchema.shape.top_p }}
				children={(field) => (
					<div className="space-y-2 lg:space-y-0">
						<Label className="flex-col items-start gap-2">
							<div className="flex items-center justify-between w-full">
								<span className="block">
									{m["settings.ai_preferences.fluency.label"]()}
								</span>
							</div>
							<span className="block text-sm tabular-nums">
								{field.state.value.toFixed(2)}
							</span>
							<Slider
								value={[field.state.value]}
								onValueChange={(value) =>
									field.handleChange(value[0] as AiPreferencesSchema["top_p"])
								}
								onBlur={field.handleBlur}
								min={0}
								max={1}
								step={0.01}
								className="py-2"
							/>
						</Label>
						<small className="text-sm text-muted-foreground block">
							{m["settings.ai_preferences.fluency.description"]()}
						</small>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
							children={([canSubmit, isSubmitting]) =>
								field.state.meta.errors ? (
									<em className="text-sm text-destructive">
										{field.state.meta.errors.join(", ")}
									</em>
								) : null
							}
						/>
					</div>
				)}
			/>
			<form.Field
				name="repetition_penalty"
				validators={{
					onChange: aiPreferencesSchema.shape.repetition_penalty,
				}}
				children={(field) => (
					<div className="space-y-2 lg:space-y-0">
						<Label className="flex-col items-start gap-2">
							<div className="flex items-center justify-between w-full">
								<span className="block">
									{m["settings.ai_preferences.repetition_avoidance.label"]()}
								</span>
							</div>
							<span className="block text-sm tabular-nums">
								{field.state.value.toFixed(2)}
							</span>
							<Slider
								value={[field.state.value]}
								onValueChange={(value) =>
									field.handleChange(
										value[0] as AiPreferencesSchema["repetition_penalty"],
									)
								}
								onBlur={field.handleBlur}
								min={0}
								max={1}
								step={0.01}
								className="py-2"
							/>
						</Label>
						<small className="text-sm text-muted-foreground block">
							{m["settings.ai_preferences.repetition_avoidance.description"]()}
						</small>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
							children={([canSubmit, isSubmitting]) =>
								field.state.meta.errors ? (
									<em className="text-sm text-destructive">
										{field.state.meta.errors.join(", ")}
									</em>
								) : null
							}
						/>
					</div>
				)}
			/>
			<div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2 mt-4">
				<form.Subscribe
					selector={(state) => [state.isDirty]}
					children={([isDirty]) => (
						<Button
							type="button"
							variant="secondary"
							onClick={() => form.reset()}
							disabled={!isDirty}
							className="w-full sm:w-auto"
						>
							{m["settings.ai_preferences.reset_button"]()}
						</Button>
					)}
				/>
				<form.Subscribe
					selector={(state) => [state.canSubmit, state.isSubmitting]}
					children={([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							disabled={!canSubmit || isSubmitting}
							className="w-full sm:w-auto"
						>
							{isSubmitting
								? m["settings.ai_preferences.save_button_saving"]()
								: m["settings.ai_preferences.save_button"]()}
						</Button>
					)}
				/>
			</div>
		</form>
	);
}
